/**
 * Job Detail Page Skeleton Component
 * Loading skeleton displayed while job data is being fetched
 */

import React from 'react';
import Skeleton from '../../../../../components/Skeleton';
import Footer from '../../../../../components/Footer';

export const JobDetailSkeleton: React.FC = () => {
  return (
    <div>
      <div className="max-w-4xl mx-auto p-2 pt-2 pb-6 sm:p-6">
        <div
          className={
            'mt-6 ' +
            'bg-white dark:bg-gray-800 shadow-md dark:shadow-gray-900 rounded-3xl px-2 md:px-8 py-8 space-y-4 transition-colors duration-200 border border-gray-100 dark:border-gray-800'
          }
        >
          {/* Breadcrumb Skeleton (now inside the card) */}
          <div className="pb-2 flex items-center text-sm text-gray-600 dark:text-gray-300">
            <Skeleton width={40} height={16} />
            <div className="mx-2">
              <Skeleton width={8} height={8} />
            </div>
            <Skeleton width={200} height={16} />
          </div>
          {/* Header Row: Role Title, Industry, and Actions */}
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            <div className="flex flex-col flex-1 min-w-0">
              <Skeleton width={220} height={24} />
              <Skeleton width={150} height={18} />
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Skeleton circle width={40} height={40} />
              <Skeleton circle width={40} height={40} />
            </div>
          </div>
          {/* People Management Tier, Role Level & Original Title Row */}
          <div className="flex flex-row justify-between items-center mb-1">
            <Skeleton width={80} height={16} />
            <Skeleton width={80} height={16} />
          </div>
          {/* Location Row */}
          <div className="flex flex-row items-center gap-2 mb-3">
            <Skeleton width={16} height={16} />
            <Skeleton width={120} height={16} />
          </div>
          {/* Job Type, Salary, Remoteness Row + Field + People Management Tier */}
          <div className="flex flex-row justify-between items-center mb-1 flex-wrap gap-2">
            <Skeleton width={80} height={16} />
            <Skeleton width={120} height={16} />
            <Skeleton width={80} height={16} />
          </div>
          {/* Dates Row */}
          <div className="flex flex-row justify-between items-center mb-1 mt-4">
            <Skeleton width={100} height={14} />
            <Skeleton width={100} height={14} />
          </div>
          {/* Language Tags Row (required + optional) */}
          <div className="flex flex-row flex-wrap items-center gap-1 mt-4">
            <Skeleton width={60} height={14} />
            <Skeleton width={60} height={14} />
            <Skeleton width={60} height={14} />
          </div>
          {/* Specialisations Row */}
          <div className="flex flex-row flex-wrap items-center gap-1 mt-2">
            <Skeleton width={80} height={14} />
            <Skeleton width={80} height={14} />
          </div>
          {/* Job Description */}
          <div className="my-4">
            <Skeleton width={180} height={20} />
            <Skeleton count={3} height={16} />
          </div>
          {/* Apply Button */}
          <Skeleton width="100%" height={40} />
          {/* Contact Email Link */}
          <Skeleton width={120} height={14} />
          {/* Requirements/Responsibilities/Company Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid-cols-1">
              <div>
                <Skeleton width={140} height={20} />
                <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300">
                  <li>
                    <Skeleton width={180} />
                  </li>
                  <li>
                    <Skeleton width={150} />
                  </li>
                  <li>
                    <Skeleton width={200} />
                  </li>
                </ul>
              </div>
              <div>
                <Skeleton width={140} height={20} />
                <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300">
                  <li>
                    <Skeleton width={180} />
                  </li>
                  <li>
                    <Skeleton width={150} />
                  </li>
                  <li>
                    <Skeleton width={120} />
                  </li>
                </ul>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <Skeleton width={80} height={14} />
                </div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-4 md:flex-col md:items-start md:gap-2">
                <Skeleton circle width={48} height={48} />
                <div>
                  <Skeleton width={120} height={18} />
                  <Skeleton width={200} height={14} />
                </div>
              </div>
            </div>
          </div>
          {/* Tabbed Section for Benefits, Recruitment, Skills */}
          <div className="pt-4">
            <div className="flex space-x-4 border-b border-gray-200 dark:border-gray-700">
              <Skeleton width={60} height={20} />
              <Skeleton width={90} height={20} />
              <Skeleton width={60} height={20} />
            </div>
            <div className="mt-4 flex flex-col gap-1 pt-2">
              <Skeleton width={120} height={14} />
              <Skeleton width={120} height={14} />
              <Skeleton width={120} height={14} />
            </div>
          </div>
          {/* Small schema badge at the bottom right */}
          <div className="flex justify-end mt-4">
            <Skeleton circle width={20} height={20} />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
